// ─────────────────────────────────────────────────────────────────────────────
// pwa_main.ts — PWA entry point for The OX Journal
//
// Replaces webview/src/main.ts for standalone PWA mode.
// Instead of acquireVsCodeApi() + postMessage, the app runs with an
// in-process StandaloneHost that persists to IndexedDB.
// ─────────────────────────────────────────────────────────────────────────────

import { StandaloneHost } from './standalone_host';
import { CanvasApp } from './canvas_app';
import { ControlBridge } from './control_bridge';
import { LatexOverlay, setLatexOverlay } from './latex_overlay';
import { AgentOrchestrator } from './agent/agent_orchestrator';
import { DEFAULT_LLM_CONFIG } from '@shared/agent_protocol';
import { ExperimentalBenchApp } from './experimental_bench';
import { APP_TITLE } from './version';

function registerServiceWorker(): void {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('./sw.js').catch(err => {
      console.warn('[PWA] Service worker registration failed:', err);
    });
  }
}

function isBenchMode(): boolean {
  const params = new URLSearchParams(window.location.search);
  return window.location.pathname.endsWith('/bench.html')
    || params.get('mode') === 'bench'
    || params.has('bench');
}

async function boot(): Promise<void> {
  const root = document.getElementById('grimoire-root');
  if (!root) throw new Error('grimoire-root not found');

  if (isBenchMode()) {
    const app = new ExperimentalBenchApp(root);
    await app.init();
    registerServiceWorker();
    window.addEventListener('beforeunload', () => {
      app.dispose();
    });
    (window as unknown as Record<string, unknown>).__oxBench = app;
    return;
  }

  // Create the standalone host (replaces VS Code extension host)
  const host = new StandaloneHost();

  // Inject the host bridge as the global acquireVsCodeApi function.
  // CanvasApp calls this in its constructor — it doesn't know the difference.
  const bridge = host.createBridge();
  (window as unknown as Record<string, unknown>).acquireVsCodeApi = () => bridge;

  // Initialise host (loads notebook from journal filesystem or IndexedDB)
  await host.init();

  // Stable app title in the tab / standalone-app chrome — independent
  // of which journal folder is open, so the tab is recognisable across
  // vaults. The journal-specific name still surfaces inside the app
  // (header, sidebar) via host.notebookTitle().
  document.title = APP_TITLE;

  // Create the canvas app — it will call acquireVsCodeApi() internally
  const app = new CanvasApp(root);

  // KaTeX DOM overlay — one transparent layer above the canvas, hosting
  // KaTeX-rendered math divs for each visible confirmed-latex block.
  const latexOverlay = new LatexOverlay(root);
  setLatexOverlay(latexOverlay);
  (window as unknown as Record<string, unknown>).__oxLatexOverlay = latexOverlay;

  // Global UI scale — shrink the whole notebook chrome to 90% of its
  // logical size so it fits comfortably in a desktop browser tab.
  // The canvas sizes itself to window / 0.9 in CSS pixels (in
  // canvas_app._resize), and root.transform: scale(0.9) compresses
  // that back down to exactly fill the window — so the visual is
  // 90% of the previous 1:1 rendering with NO black bars on any
  // viewport aspect. KaTeX overlay shares the root transform so it
  // stays aligned with the canvas. Pointer events get
  // correctness-corrected inside CanvasApp via getBoundingClientRect
  // vs canvas.offsetWidth.
  root.style.transformOrigin = '0 0';
  root.style.transform = 'scale(0.9)';

  // Expose for dev/debug console access
  (window as unknown as Record<string, unknown>).__oxApp = app;
  (window as unknown as Record<string, unknown>).__oxHost = host;

  // Wire host → webview messages (the host sends to CanvasApp.onHostMessage)
  host.setSendToWebview((msg: unknown) => {
    app.onHostMessage(msg as Parameters<typeof app.onHostMessage>[0]);
  });

  // ── Agent subsystem ──────────────────────────────────────────────────────
  // Initialize the LLM agent orchestrator.  The API key is loaded from
  // localStorage; if absent, falls back to the built-in dev key.
  const DEV_KEY = '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';
  const apiKey = localStorage.getItem('ox-agent-api-key') || DEV_KEY;
  if (apiKey) {
    const agent = new AgentOrchestrator();
    const config = {
      ...DEFAULT_LLM_CONFIG,
      apiKey,
    };
    const dispatch = (action: import('@shared/grimoire_action').GrimoireAction) => {
      // Send to host for persistence + echo back to app via setSendToWebview
      bridge.postMessage({ type: 'action', payload: action });
    };
    await agent.init(config, dispatch);

    // Auto-bind the fold for beta so characters are immediately accessible
    const fold = await agent.getFoldState();
    if (!fold.bounded) {
      await agent.bindHouse();
      // Wire room → page ID mappings
      const roomPageMap: Record<string, string> = {
        'hall': 'fold_hall',
        'study': 'fold_study',
        'kitchen': 'fold_kitchen',
        'ireth-chamber': 'fold_ireth_chamber',
        'laura-room': 'fold_laura_room',
        'tower': 'fold_tower',
        'patio': 'fold_patio',
        'lounge': 'fold_lounge',
        'basement': 'fold_basement',
      };
      const updatedFold = await agent.getFoldState();
      for (const room of updatedFold.rooms) {
        if (roomPageMap[room.id]) room.pageId = roomPageMap[room.id];
      }
      // Also add Laura as an NPC in her room for beta
      if (!updatedFold.npcs.find((n: { persona: string }) => n.persona === 'laura')) {
        updatedFold.npcs.push({
          persona: 'laura' as import('@shared/agent_protocol').PersonaId,
          roomId: 'laura-room' as import('@shared/the_fold').RoomId,
          activity: 'reading by the lamp',
          mood: 'calm' as import('@shared/agent_protocol').NpcMood,
          lastInteraction: 0,
        });
      }
      await agent.updateNpc('ireth', { roomId: 'hall' as import('@shared/the_fold').RoomId });
      // Save the full state with room mappings
      const mem = (agent as unknown as { memory: { saveFoldState(s: unknown): Promise<void> } }).memory;
      await mem.saveFoldState(updatedFold);
      console.log('[agent] fold auto-bound for beta — Ireth & Laura active');
    }

    // Always sync fold state to notebook_state (agent memory persists across
    // reloads, but notebook_state only tracks fold via dispatched actions)
    const syncFold = await agent.getFoldState();
    if (syncFold.bounded) {
      const now = Date.now();
      dispatch({ type: 'fold.bind', origin: 'system', timestamp: now } as import('@shared/grimoire_action').GrimoireAction);
      for (const npc of syncFold.npcs) {
        dispatch({ type: 'fold.npc.update', persona: npc.persona, roomId: npc.roomId, mood: npc.mood, activity: npc.activity, origin: 'system', timestamp: now } as import('@shared/grimoire_action').GrimoireAction);
      }
    }

    app.setAgent(agent);
    (window as unknown as Record<string, unknown>).__oxAgent = agent;
    console.log('[agent] orchestrator initialised');
  } else {
    console.log('[agent] no API key — agent disabled (set via localStorage "ox-agent-api-key")');
  }

  // ── Control bridge ───────────────────────────────────────────────────────
  // Exposes window.grimoire for in-browser use, and connects to the
  // run_pwa.py control server for remote control (Claude skill, curl, …).
  // Safe to fail: if the server has no /api/* endpoints, the bridge just
  // keeps polling quietly.
  const controlBridge = new ControlBridge(host, () => app as unknown as { _canvas: HTMLCanvasElement; onHostMessage(msg: unknown): void });
  (window as unknown as Record<string, unknown>).grimoire = controlBridge.api;
  (window as unknown as Record<string, unknown>).__oxControlBridge = controlBridge;
  controlBridge.start().catch(err => console.warn('[control-bridge] start failed', err));

  // Register the service worker for PWA installability + offline support
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('./sw.js').catch(err => {
      console.warn('[PWA] Service worker registration failed:', err);
    });
  }

  // Clean up on page unload
  window.addEventListener('beforeunload', () => {
    host.dispose();
  });
}

// Wait for DOM, then boot
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => boot().catch(console.error));
} else {
  boot().catch(console.error);
}
